"use client"
import writeText from "copy-to-clipboard"
import { motion } from "motion/react"
import type React from "react"
import { memo, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { cn } from "@/utils/cn"

export const TextRevealCard = ({
  text,
  revealText,
  children,
  className,
}: {
  text: string
  revealText: string
  children?: React.ReactNode
  className?: string
}) => {
  const [widthPercentage, setWidthPercentage] = useState(0)
  // biome-ignore lint/suspicious/noExplicitAny: AceternityUI
  const cardRef = useRef<HTMLDivElement | any>(null)
  const [left, setLeft] = useState(0)
  const [localWidth, setLocalWidth] = useState(0)
  const [isMouseOver, setIsMouseOver] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  useEffect(() => {
    if (cardRef.current) {
      const { left, width: localWidth } = cardRef.current.getBoundingClientRect()
      setLeft(left)
      setLocalWidth(localWidth)
    }
  }, [])

  // biome-ignore lint/suspicious/noExplicitAny: AceternityUI
  function mouseMoveHandler(event: any) {
    event.preventDefault()

    const { clientX } = event
    if (cardRef.current) {
      const relativeX = clientX - left
      setWidthPercentage((relativeX / localWidth) * 100)
    }
  }

  function mouseLeaveHandler() {
    setIsMouseOver(false)
    setWidthPercentage(0)
  }
  function mouseEnterHandler() {
    setIsMouseOver(true)
  }
  function touchMoveHandler(event: React.TouchEvent<HTMLDivElement>) {
    event.preventDefault()
    // biome-ignore lint/style/noNonNullAssertion: AceternityUI
    const clientX = event.touches[0]!.clientX
    if (cardRef.current) {
      const relativeX = clientX - left
      setWidthPercentage((relativeX / localWidth) * 100)
    }
  }

  const rotateDeg = (widthPercentage - 50) * 0.1
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: AceternityUI
    <div
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
      onMouseMove={mouseMoveHandler}
      onTouchStart={mouseEnterHandler}
      onTouchEnd={mouseLeaveHandler}
      onTouchMove={touchMoveHandler}
      onClick={() => {
        writeText(revealText)
        setIsCopied(true)
      }}
      ref={cardRef}
      className={cn(
        "relative w-full overflow-hidden rounded-lg border bg-gray-200 p-8 dark:bg-gray-900",
        className,
      )}
    >
      {children}

      <div className="relative flex h-40 items-center overflow-hidden text-center!">
        <motion.div
          style={{
            width: "100%",
          }}
          animate={
            isMouseOver
              ? {
                  opacity: widthPercentage > 0 ? 1 : 0,
                  clipPath: `inset(0 ${100 - widthPercentage}% 0 0)`,
                }
              : {
                  clipPath: `inset(0 ${100 - widthPercentage}% 0 0)`,
                }
          }
          transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
          className="absolute z-20 bg-gray-200 will-change-transform dark:bg-gray-900"
        >
          <p
            style={{
              textShadow: "4px 4px 15px rgba(0,0,0,0.15)",
            }}
            className="bg-linear-to-b from-foreground/60 to-foreground/90 bg-clip-text pt-5 pb-10 font-extrabold font-mono text-base text-transparent sm:text-[3rem]"
          >
            {revealText}
          </p>
          <p className="-mt-5 text-muted-foreground/70 text-sm">
            {isCopied ? "Copied to clipboard!" : "Click to copy code"}
          </p>
        </motion.div>
        <motion.div
          animate={{
            left: `${widthPercentage}%`,
            rotate: `${rotateDeg}deg`,
            opacity: widthPercentage > 0 ? 1 : 0,
          }}
          transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
          className="absolute z-50 h-40 w-[8px] bg-linear-to-b from-transparent via-gray-300 to-transparent will-change-transform dark:via-gray-700"
        />

        <div className="mask-[linear-gradient(to_bottom,transparent,white,transparent)] mx-auto overflow-hidden">
          <p className="bg-muted-foreground/40 bg-clip-text py-14 font-bold text-base text-transparent sm:text-[3rem] dark:bg-muted-foreground/25">
            {text}
          </p>
          <MemoizedStars />
        </div>
      </div>
    </div>
  )
}

export const TextRevealCardTitle = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <h2 className={twMerge("mb-2 text-lg text-white", className)}>{children}</h2>
}

export const TextRevealCardDescription = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return <p className={twMerge("text-[#a9a9a9] text-sm", className)}>{children}</p>
}

const Stars = () => {
  const randomMove = () => Math.random() * 4
  const randomOpacity = () => Math.random()
  const random = () => Math.random()
  return (
    <div className="absolute inset-0">
      {[...Array(80)].map((_, i) => (
        <motion.span
          key={`star-${i}`}
          animate={{
            top: `calc(${random() * 100}% + ${randomMove()}px)`,
            left: `calc(${random() * 100}% + ${randomMove()}px)`,
            opacity: randomOpacity(),
            scale: [1, 1.5, 0],
          }}
          transition={{
            duration: random() * 10 + 20,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            top: `${random() * 100}%`,
            left: `${random() * 100}%`,
            width: `2px`,
            height: `2px`,
            borderRadius: "50%",
            zIndex: 1,
          }}
          className="inline-block bg-purple-500"
        />
      ))}
    </div>
  )
}

export const MemoizedStars = memo(Stars)
